import { defineStore } from "#src/stores/state-wrapper.js";

import {
  useEntityStore,
  entityKeyGen,
  ENTITY_TYPE,
} from "#src/stores/entity.js";
import {
  useIndividualStore,
  individualKeyGen,
} from "#src/stores/individual.js";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.js";
import { useExistingCoverageStore } from "#src/stores/existing-coverage.js";
import {
  INSURED_TYPES,
  useInsuredStore,
  usePrimaryInsuredStore,
  useJointInsuredStore,
  getPayorRoleForInsured,
} from "#src/stores/insured.js";
import { useQuotingStore } from "#src/stores/quoting.js";
import { useFlowStore } from "#src/stores/flow.js";
import { useCaseStore } from "#src/stores/case.js";
import { useStepOwnerStore } from "#src/stores/step-owner.js";
import { useStepQuoteStore } from "#src/stores/step-quote.js";

import { ROLE_TYPE } from "#src/types/Role.ts";

import {
  currencyFormatter,
  parseErrorMessage,
  generateUuid,
  clone,
} from "#src/util/helpers.js";
import { ProductType, categoryToConstantProduct } from "#src/types/Product.ts";
import { PRODUCTS } from "#src/constants/products.ts";
import { CONCEPTS } from "#src/constants/concepts.ts";
import { setAgentFromRequest } from "#src/types/Agent.ts";
import { InternalQuote } from "#src/types/Quote.ts";

import { HttpIndividualsService } from "#src/services/http-individuals.service.js";
import { EntityService } from "#src/services/entity.service.js";
import { CoverageService } from "#src/services/coverage.service.js";
import { EappService } from "#src/services/eapp.service.js";
import { QuotesService } from "#src/services/quotes.service.js";
import { ProductsService } from "#src/services/products.service.js";

import { PARTNER_DISCOUNT } from "#src/types/QuoteParams.ts";
import {
  BEST_TIME_TO_CALL_OPTIONS,
  PURPOSE_OPTIONS,
} from "#src/constants/refer-info.ts";
import { useSavableProperty } from "#src/composables/savable-property.composable.js";
import {
  validateBoolean,
  validateInList,
  validateNumber,
  validateText,
} from "#src/composables/savable-property-validators.mjs";
import { setApprovedDomainProductsFromRequest } from "#src/types/ApprovedDomain.ts";
import { STEP } from "#src/constants/step.ts";
import { ADDRESS_TYPE } from "#src/types/Address.ts";

export const PARTY_TYPE = {
  INDIVIDUAL: "Individual",
  ENTITY: "Entity",
};

export const getPartyStore = (partyKey, pinia) => {
  if (!partyKey) {
    if (import.meta.env.DEV) {
      console.trace("Missing Party Key");
    }
    throw "MISSING PARTY KEY";
  }
  if (Object.values(INSURED_TYPES).includes(partyKey))
    return useInsuredStore(partyKey, pinia);
  if (partyKey.includes("entity")) return useEntityStore(partyKey, pinia);
  return useIndividualStore(partyKey, pinia);
};

export function useEappStore(pinia, hot) {
  const individualService = new HttpIndividualsService(pinia);
  const entityService = new EntityService(pinia);
  const coverageService = new CoverageService(pinia);
  const quotesService = new QuotesService(pinia);
  const eAppService = new EappService(pinia);
  const productsService = new ProductsService(pinia);

  return defineStore("electronic-application", {
    state: () => ({
      has_logged_in: false,
      has_created_eapp: false,
      advanced_health: false,
      assistance: { topPriority: "", ltcType: "" },
      apply_link: null,
      existing_coverages: [],
      id: null,
      uuid: null,

      calc_children: useSavableProperty({
        requestMap: "calc_children",
        group: "root",
        initialValue: [],
        rules: {},
        requestFormatter: value => value.map(a => ({ age: a })),
        eachRules: {
          greaterThanOrEqualToZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_mortgage_balance: useSavableProperty({
        requestMap: "calc_mortgage_balance",
        group: "root",
        initialValue: 145000,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_non_mortgage_debts: useSavableProperty({
        requestMap: "calc_non_mortgage_debts",
        group: "root",
        initialValue: 32000,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_annual_beneficiary_income: useSavableProperty({
        requestMap: "calc_annual_beneficiary_income",
        group: "root",
        initialValue: 50000,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_beneficiary_income_years: useSavableProperty({
        requestMap: "calc_beneficiary_income_years",
        group: "root",
        initialValue: 10,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_existing_life_insurance: useSavableProperty({
        requestMap: "calc_existing_life_insurance",
        group: "root",
        initialValue: 0,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),
      calc_savings: useSavableProperty({
        requestMap: "calc_savings",
        group: "root",
        initialValue: 24000,
        rules: {
          greaterThanZero: validateNumber({ greaterThanOrEqualTo: 0 }),
        },
      }),

      demo_name: useSavableProperty({
        requestMap: "demo_name",
        group: "root",
        rules: {
          validLength: validateText({ minLength: 1, maxLength: 255 }),
        },
      }),
      purpose_of_insurance: useSavableProperty({
        requestMap: "purpose_of_insurance",
        group: "root",
        rules: {
          isInList: validateInList(PURPOSE_OPTIONS),
        },
      }),
      best_time_to_call: useSavableProperty({
        requestMap: "best_time_to_call",
        group: "root",
        rules: {
          isInList: validateInList(BEST_TIME_TO_CALL_OPTIONS),
        },
      }),
      refer_notes: useSavableProperty({
        requestMap: "refer_notes",
        group: "root",
        rules: {
          validLength: validateText({ minLength: 1, maxLength: 5000 }),
        },
      }),
      send_refer_text: useSavableProperty({
        requestMap: "send_refer_text",
        group: "root",
        rules: {
          isTrueOrFalse: validateBoolean(),
        },
      }),
      round_robin_agent_id: useSavableProperty({
        requestMap: "round_robin_agent_id",
        group: "root",
        rules: {
          isInList: {
            v: () => {
              const instance = useInstanceSettingsStore(pinia);
              const store = useEappStore(pinia);
              return instance.round_robin_agents.some(
                v =>
                  v.value === store.round_robin_agent_id.model ||
                  store.round_robin_agent_id.model === null
              );
            },
            message: "Please specify an agent",
          },
        },
      }),
      other_pending_insurance_amount_to_be_accepted: useSavableProperty({
        requestMap: "other_pending_insurance_amount_to_be_accepted",
        group: "root",
        rules: {
          isNumber: validateNumber({ greaterThanOrEqualTo: 0 }),
          isMoreThanMin: {
            v: () => {
              const eapp = useEappStore(pinia);
              if (!eapp.otherPendingAmountToBeAcceptedMin) return true;
              return (
                eapp.otherPendingAmountToBeAcceptedMin <=
                eapp.other_pending_insurance_amount_to_be_accepted.model
              );
            },
            message: () => {
              const eapp = useEappStore(pinia);
              return `Must be greater than or equal to ${currencyFormatter(
                eapp.otherPendingAmountToBeAcceptedMin
              )}`;
            },
          },
          isLessThanPendingCoverage: {
            v: () => {
              const eapp = useEappStore(pinia);

              if (!eapp.otherPendingAmountToBeAcceptedMax) return true;
              return (
                eapp.other_pending_insurance_amount_to_be_accepted.model <=
                eapp.otherPendingAmountToBeAcceptedMax
              );
            },
            message: () => {
              const eapp = useEappStore(pinia);
              `Must be less than or equal to  ${currencyFormatter(
                eapp.otherPendingAmountToBeAcceptedMax
              )}`;
            },
          },
          isGreaterThanZero: validateNumber({ greaterThan: 0 }),
        },
      }),
      tia: useSavableProperty({
        requestMap: "tia",
        group: "root",
        rules: {
          isTrueOrFalse: validateBoolean(),
        },
      }),

      refer: false,
      npn: "",
      parent_url: "",
      parties: [],
      // this beast should have its own store...
      selectedQuote: InternalQuote(),
      sharable_apply_link: null,
      signer_status: null,
      step_is_saving: false,
      resume_carrier_eapp: null,
      limit_carrier_eapp_to_insured: null,
      use_eachother_as_beneficiaries: null,
      conversion: false,
    }),
    getters: {
      isAdvisorUseOnly() {
        return this.selectedQuote.product.advisor_use_only === true;
      },
      isMultiplePolicies() {
        return (
          this.isDualInsured && this.selectedQuote?.product?.multiple_policies
        );
      },
      carrierName() {
        return this.selectedQuote?.carrier?.name;
      },
      selectedQuoteCategories() {
        return categoryToConstantProduct(this.selectedQuote?.product?.category);
      },
      pendingExistingCoverages() {
        return this.existing_coverages.filter(id => {
          const coverage = useExistingCoverageStore(id, pinia);
          return coverage.status.model === "Pending";
        });
      },
      hasPendingExistingCoverages() {
        return Boolean(this.pendingExistingCoverages.length);
      },
      otherPendingAmountToBeAcceptedMin() {
        return (
          this.selectedQuote?.death_benefit ||
          this.selectedQuote?.face_amount ||
          this.selectedQuote?.initial_death_benefit ||
          0
        );
      },
      otherPendingAmountToBeAcceptedMax() {
        if (!this.hasPendingExistingCoverages) return null;
        return this.pendingExistingCoverages.reduce(
          (accumulator, coverageKey) => {
            const coverage = useExistingCoverageStore(coverageKey, pinia);
            return coverage.face_amount.model + accumulator;
          },
          this.otherPendingAmountToBeAcceptedMin
        );
      },
      isDualInsured() {
        const quoting = useQuotingStore(pinia);

        const latestParams = quoting.rollbackPoint;

        if (latestParams.partner_discount === PARTNER_DISCOUNT.JOINT) {
          return [PRODUCTS.LTC, PRODUCTS.LINKED_BENEFIT].includes(
            latestParams.selected_type
          );
        } else if (latestParams.selected_type === PRODUCTS.INCOME_RIDER) {
          return latestParams.joint_income;
        }

        return false;
      },
      beneficiariesByInsuredId() {
        const insured = usePrimaryInsuredStore(pinia);
        const jInsured = useJointInsuredStore(pinia);

        const byInsuredId = {};

        if (insured.id) byInsuredId[insured.id] = [];
        if (jInsured.id) byInsuredId[jInsured.id] = [];

        this.parties.forEach(partyKey => {
          const store = getPartyStore(partyKey, pinia);
          const primaryRoleForId = store.role_for_id[ROLE_TYPE.PRIMARY_BENEFICIARY];
          const contingentRoleForId =
            store.role_for_id[ROLE_TYPE.CONTINGENT_BENEFICIARY];
          if (primaryRoleForId) byInsuredId[primaryRoleForId].push(partyKey);

          if (contingentRoleForId)
            byInsuredId[contingentRoleForId].push(partyKey);
        });

        if (
          jInsured.id &&
          (insured.roles[ROLE_TYPE.PRIMARY_BENEFICIARY] ||
            insured.roles[ROLE_TYPE.CONTINGENT_BENEFICIARY])
        ) {
          byInsuredId[jInsured.id].push(INSURED_TYPES.INSURED);
        }

        if (
          (jInsured.id && jInsured.roles[ROLE_TYPE.PRIMARY_BENEFICIARY]) ||
          jInsured.roles[ROLE_TYPE.CONTINGENT_BENEFICIARY]
        ) {
          byInsuredId[insured.id].push(INSURED_TYPES.JOINT);
        }
        return byInsuredId;
      },
      existingCoveragesByInsuredId() {
        const groups = {};

        this.existing_coverages.forEach(id => {
          const coverage = useExistingCoverageStore(id, pinia);
          let coverageBelongsTo = coverage.insured_id;
          if (!groups[coverageBelongsTo]) groups[coverageBelongsTo] = [];
          groups[coverageBelongsTo].push(id);
        });

        return groups;
      },
      ownerKey() {
        const ownerStep = useStepOwnerStore(pinia);
        // If users may not specify who the owner is, then it's assumed to be the insured (such as accidental death)
        if (ownerStep.inactive) return INSURED_TYPES.INSURED;

        return [
          INSURED_TYPES.INSURED,
          INSURED_TYPES.JOINT,
          ...this.parties,
        ].find(p => getPartyStore(p, pinia).roles[ROLE_TYPE.OWNER]);
      },
    },
    actions: {
      initializeCalculatorFromEapp({
        calc_beneficiary_income_years,
        calc_annual_beneficiary_income,
        calc_children,
        calc_existing_life_insurance,
        calc_mortgage_balance,
        calc_non_mortgage_debts,
        calc_savings,
      }) {
        const settable = v => Boolean(v || v === 0);

        if (settable(calc_beneficiary_income_years)) {
          this.calc_beneficiary_income_years.load(
            calc_beneficiary_income_years
          );
        }

        if (settable(calc_annual_beneficiary_income)) {
          this.calc_annual_beneficiary_income.load(
            calc_annual_beneficiary_income
          );
        }
        if (Array.isArray(calc_children)) {
          this.calc_children.load(calc_children.map(a => a.age));
        }

        if (settable(calc_existing_life_insurance)) {
          this.calc_existing_life_insurance.load(calc_existing_life_insurance);
        }

        if (settable(calc_mortgage_balance)) {
          this.calc_mortgage_balance.load(calc_mortgage_balance);
        }

        if (settable(calc_non_mortgage_debts)) {
          this.calc_non_mortgage_debts.load(calc_non_mortgage_debts);
        }

        if (settable(calc_savings)) {
          this.calc_savings.load(calc_savings);
        }
      },
      initializeQuoteData(fullQuote, partialPrimaryQuote, partialJointQuote) {
        if (fullQuote && partialPrimaryQuote) {
          fullQuote.real_id = partialPrimaryQuote.id;
          if (partialJointQuote) {
            fullQuote.real_joint_id = partialJointQuote.id;
            fullQuote.joint_insured_premium = partialJointQuote.premium;
            fullQuote.joint_insured_validated_rating =
              partialJointQuote.validated_rating;
          }
          this.setSelectedQuote(fullQuote);
        }
      },
      initializeFromEApp(value) {
        if (!value) return;
        this.parent_url = value.url;
        this.id = value.id;
        this.uuid = value.uuid;
        this.assistance.topPriority = value.assist?.top_priority;
        this.assistance.ltcType = value.assist?.ltc_type;

        this.purpose_of_insurance.load(value.purpose_of_insurance);
        this.best_time_to_call.load(value.best_time_to_call);
        this.conversion = Boolean(value.conversion);
        this.refer_notes.load(value.refer_notes);
        this.send_refer_text.load(value.send_refer_text);
        this.tia.load(value.tia);
        this.demo_name.load(value.demo_name);

        this.refer = value.refer;
        this.sharable_apply_link = value.sharable_apply_link;
        this.signer_status = value.signer_status;
        this.limit_carrier_eapp_to_insured =
          value.limit_carrier_eapp_to_insured;
        this.resume_carrier_eapp = value.resume_carrier_eapp;
        this.other_pending_insurance_amount_to_be_accepted.load(
          value.other_pending_insurance_amount_to_be_accepted
        );
      },
      initializeInsured(type, value) {
        const insuredStore = useInsuredStore(type, pinia);
        insuredStore.setFromEApp(value);
      },
      initializeParties(rawParties) {
        this.parties.splice(0, this.parties.length);
        rawParties.forEach(party => {
          const isInsured = party.application_roles.find(
            ({ role }) => role === ROLE_TYPE.INSURED
          );
          const isJointInsured = party.application_roles.find(({ role }) =>
            [ROLE_TYPE.JOINT_INSURED, ROLE_TYPE.SPOUSE].includes(role)
          );

          if (isInsured) {
            this.initializeInsured(INSURED_TYPES.INSURED, party);
          } else if (isJointInsured) {
            this.initializeInsured(INSURED_TYPES.JOINT, party);
          } else if (party.type === "Individual") {
            const individualStore = useIndividualStore(
              individualKeyGen(party.id),
              pinia
            );
            individualStore.initializeFromEapp(party);
            this.parties.push(individualStore.store_id);
            return;
          } else if (party.type === "Entity") {
            const entityStore = useEntityStore(entityKeyGen(party.id), pinia);
            entityStore.initializeFromEapp(party);
            this.parties.push(entityStore.store_id);
          }
        });
      },
      initializeExistingCoverages(rawExistingCoverages) {
        rawExistingCoverages.forEach(rawCoverage => {
          const coverage = useExistingCoverageStore(
            `existing-coverage-${rawCoverage.id}`,
            pinia
          );
          coverage.initializeFromEapp(rawCoverage);
          this.existing_coverages.push(`existing-coverage-${rawCoverage.id}`);
        });
      },
      // TODO: Delete and replace with createOwnerParty / createBeneficiaryParty
      async createParty({ type, beneficiary_amount = null, role, insured_id }) {
        if (
          ![
            PARTY_TYPE.INDIVIDUAL,
            ENTITY_TYPE.BUSINESS,
            ENTITY_TYPE.TRUST,
          ].includes(type)
        )
          throw "Missing Party Type";

        const application_roles_attributes = [];
        if (Array.isArray(role)) {
          role.forEach(r => {
            const roles_attributes = { role: r };
            if (beneficiary_amount) {
              roles_attributes.beneficiary_amount = +(
                beneficiary_amount / 100
              ).toFixed(2);
            }

            if (insured_id) roles_attributes.insured_id = insured_id;
            application_roles_attributes.push(roles_attributes);
          });
        } else {
          const roles_attributes = { role };
          if (beneficiary_amount) {
            roles_attributes.beneficiary_amount = +(
              beneficiary_amount / 100
            ).toFixed(2);
          }

          if (insured_id) roles_attributes.insured_id = insured_id;
          application_roles_attributes.push(roles_attributes);
        }

        return this.createNewParty({ type, application_roles_attributes });
      },
      async createPayorParty({ type, insured_id }) {
        let insuredId = insured_id;
        if (!Array.isArray(insuredId)) insuredId = [insuredId];

        const application_roles_attributes = [];
        insuredId.forEach(i => {
          application_roles_attributes.push({
            role: getPayorRoleForInsured(i, pinia),
            insured_id: i,
          });
        });

        return this.createNewParty({ type, application_roles_attributes });
      },
      async createNewParty({
        type,
        application_roles_attributes,
        addToParties = true,
      }) {
        if (
          ![
            PARTY_TYPE.INDIVIDUAL,
            ENTITY_TYPE.BUSINESS,
            ENTITY_TYPE.TRUST,
          ].includes(type)
        )
          throw "Missing Party Type";

        const body = {
          addresses_attributes: [],
          application_roles_attributes,
          irrevocable: undefined,
        };

        let address_type = ADDRESS_TYPE.BUSINESS;

        if (type === PARTY_TYPE.INDIVIDUAL) {
          address_type = ADDRESS_TYPE.HOME;
        }

        body.addresses_attributes.push({ address_type });

        if (type === ENTITY_TYPE.TRUST) body.irrevocable = false;

        let party, model;
        if (type === PARTY_TYPE.INDIVIDUAL) {
          party = await individualService.createIndividual(body);
          model = useIndividualStore(individualKeyGen(party.id), pinia);
        } else {
          party = await entityService.createEntity(body);
          model = useEntityStore(entityKeyGen(party.id), pinia);
        }

        model.initializeFromEapp(party);
        if (addToParties) this.parties.push(model.store_id);
        return model.store_id;
      },
      async deletePartyOrRole({ partyKey, role }) {
        const store = getPartyStore(partyKey, pinia);
        const roles = Object.values(store.roles).filter(Boolean);

        if (
          (role && roles.length > 1) ||
          Object.values(INSURED_TYPES).includes(partyKey)
        ) {
          return await store.deleteRole(role);
        }
        let existingCoverageCount = 0;
        this.existing_coverages.forEach(coverage => {
          const existingCoverage = useExistingCoverageStore(coverage, pinia);
          if (existingCoverage.insurable.model === partyKey)
            ++existingCoverageCount;
          if (existingCoverage.ownerable.model === partyKey)
            ++existingCoverageCount;
        });

        if (existingCoverageCount > 1) return;

        await store.deleteParty();
        const index = this.parties.findIndex(v => v === partyKey);
        if (index > -1) this.parties.splice(index, 1);
      },
      setSelectedQuote(newPrimaryQuote) {
        this.selectedQuote = clone(newPrimaryQuote);
      },
      updateSelectedQuote(newPrimaryQuote) {
        const quote = { ...newPrimaryQuote, primary: true };
        const quoting = useQuotingStore(pinia);

        let type = quoting.params.selected_type;
        if (quoting.params.selected_type === CONCEPTS.ALL_UL) {
          type = quoting.params.subtype || quoting.params.selected_type;
        }
        const product = new ProductType(type);

        const insured = usePrimaryInsuredStore(pinia);
        const params = {
          ...product.eAppParser({
            ...quote,
            inflation_percentage:
              quote.inflation_percentage === undefined
                ? null
                : quote.inflation_percentage,
            face_amount: quote.initial_death_benefit,
            active_years: quote.product.active_years,
            premium: quote.premium,
            age: insured.age,
          }),
        };
        quoting.initializeQuoteParams(params);
        quoting.setRollbackPoint();
        return quote;
      },
      async updateOrCreateEapp(quote) {
        if (this.id) return this.saveQuoteStep(quote);
        await this.createAndPrefill(quote);
        return { created: true };
      },
      setQuoteMetadata(quote) {
        if (!quote?.product?.id) return;
        this.primaryQuoteId = quote.product?.id;
        this.setSelectedQuote(quote);
      },
      async createCoverage(insured_id) {
        const coverage = await coverageService.createCoverage({ insured_id });
        const coverageStore = useExistingCoverageStore(
          `existing-coverage-${coverage.id}`,
          pinia
        );
        coverageStore.initializeFromEapp(coverage);
        this.existing_coverages.push(`existing-coverage-${coverage.id}`);
      },
      async deleteCoverage(id) {
        const coverage = useExistingCoverageStore(id, pinia);
        const coverageId = coverage.id;
        const coverageIndex = this.existing_coverages.indexOf(id);

        await coverageService.deleteCoverage(coverageId);

        this.existing_coverages.splice(coverageIndex, 1);
        coverage.$dispose();
      },
      async updateEapp(reqObj) {
        const { data_requirements } =
          await eAppService.updateElectronicApplication(reqObj);
        const flow = useFlowStore(pinia);
        flow.updateStepRequirements(data_requirements);
      },
      async saveAttributes(attributes = []) {
        if (!attributes.length || !this.id) return;
        const reqObj = {};
        const reqAttrs = [];
        attributes.forEach(a => {
          if (a in this) {
            if (this[a]?.format) {
              const key = this[a].requestMap;
              const value = this[a].format();
              reqAttrs.push(a);
              reqObj[key] = value;
            } else {
              reqObj[a] = this[a];
            }
          }
        });

        if (!Object.keys(reqObj).length) return;
        const reqUuid = generateUuid();
        if (reqAttrs.length) {
          reqAttrs.forEach(a => {
            this[a].requests.push({
              id: reqUuid,
              initiatedAt: new Date().getTime(),
              payload: JSON.stringify(reqObj),
            });
          });
        }
        let res, errorMessage;
        try {
          res = await this.updateEapp(reqObj);
        } catch (e) {
          errorMessage = parseErrorMessage(e);
        } finally {
          reqAttrs.forEach(a => {
            this[a].deleteRequest(reqUuid);

            if (errorMessage) this[a].errorMessage = errorMessage;
          });
        }

        return res;
      },
      validateCitizenship(visaType) {
        return productsService.validateCitizenship(
          this.selectedQuote.product?.id,
          visaType
        );
      },
      async ensureRoleMatchForDualApplicants() {
        // lets say partner discount is one and partner is spouse, then they change it to joint
        // partner discount that comes through is "both"

        const jointInsured = useJointInsuredStore(pinia);
        if (jointInsured.id && this.isDualInsured) {
          if (jointInsured.roles[ROLE_TYPE.JOINT_INSURED]) return;
          // joint should have joint insured role
          const id = await individualService.giveJointInsuredRole(
            jointInsured.id
          );
          jointInsured.roles[ROLE_TYPE.SPOUSE] = null;
          jointInsured.roles[ROLE_TYPE.JOINT_INSURED] = id;
        } else if (jointInsured.id) {
          if (jointInsured.roles[ROLE_TYPE.SPOUSE]) return;
          // joint should have spouse role
          const id = await individualService.giveSpouseRole(jointInsured.id);
          jointInsured.roles[ROLE_TYPE.JOINT_INSURED] = null;
          jointInsured.roles[ROLE_TYPE.SPOUSE] = id;
        }
      },
      notifyUnlicensedAgent(not_licensed_state) {
        return eAppService.notifyUnlicensedAgent(not_licensed_state);
      },
      setIds({ id = null, uuid = null }) {
        try {
          if (id) sessionStorage.setItem("eapp_id", id);
          else sessionStorage.removeItem("eapp_id");

          if (uuid) sessionStorage.setItem("eapp_uuid", uuid);
          else sessionStorage.removeItem("eapp_uuid");
          // eslint-disable-next-line no-unused-vars
        } catch (e) {
          // do nothing
        }
        this.id = id;
        this.uuid = uuid;
      },
      async fetchEapp({ birthdate = null } = {}) {
        let body = {};
        if (birthdate) body.birthdate = birthdate;
        const eApp = await eAppService.fetchEapp(body);
        pinia._s.forEach(store => {
          // keep current step + instance settings
          if (["instance-settings", "flow", "test-helpers"].includes(store.$id))
            return;
          store.$reset();
        });
        await this.initializeApplicationData(eApp, birthdate);
        return eApp;
      },
      async resumeEApp(resume, id, uuid, attested) {
        const eApp = await eAppService.resumeEApp(resume, id, uuid);
        await this.initializeApplicationData(eApp);
        const instance = useInstanceSettingsStore(pinia);
        instance.isAgent = true;
        const flow = useFlowStore(pinia);
        let step = eApp.step;
        if (attested === "true" && eApp.step === STEP.QUOTE) {
          step = useStepQuoteStore(pinia).next;
        }
        await flow.navToStep({ step });
        return true;
      },
      async createElectronicApplication(
        quote = {},
        additionalCreateOptions = {}
      ) {
        const quoting = useQuotingStore(pinia);
        const flow = useFlowStore(pinia);
        const instance = useInstanceSettingsStore(pinia);
        const insured = usePrimaryInsuredStore(pinia);

        const body = {
          advanced_health: this.advanced_health,
          approved_domain_id: instance.id,
          assist: {
            enabled: true,
            top_priority: this.assistance.topPriority,
            ltc_type: this.assistance.ltcType,
          },
          calc_annual_beneficiary_income:
            this.calc_annual_beneficiary_income.model,
          calc_beneficiary_income_years:
            this.calc_beneficiary_income_years.model,
          calc_children: this.calc_children.model.map(age => ({ age })),
          calc_existing_life_insurance: this.calc_existing_life_insurance.model,
          calc_mortgage_balance: this.calc_mortgage_balance.model,
          calc_non_mortgage_debts: this.calc_non_mortgage_debts.model,
          calc_savings: this.calc_savings.model,
          chat: {
            initiated_step: null,
            enabled: Boolean(instance.chat),
            opened: false,
            created_via_chat: null,
          },
          demo_name: this.demo_name.model,
          initiated_url: this.parent_url,
          insured: insured.getCreateData(quote.id),
          owner: { same_as_insured: true },
          rank: quote.rank,
          refer: this.refer,
          selected_type: quoting.params.selected_type,
          step: flow.step,
          url: this.parent_url,
          prefill_params: instance.prefill,
        };

        if (additionalCreateOptions?.created_without_quote) {
          body.created_without_quote = true;
        }

        if (instance.metadata.value) {
          body.metadata = instance.metadata.value;
        }
        if (instance.test) body.test = true;

        if (quoting.params.selected_type === CONCEPTS.ALL_UL) {
          body.selected_type = quoting.params.subtype || CONCEPTS.ALL_UL;
        }

        if (quoting.params.partner_discount) {
          let key = "joint_insured";
          if (quoting.params.partner_discount === "one") key = "spouse";

          const joint = useJointInsuredStore(pinia);
          body[key] = joint.getCreateData(quote.joint_insured_quote_id);
        } else if (quote.joint_insured_quote_id) {
          body.joint_insured = { quote_id: quote.joint_insured_quote_id };
        } else if (quoting.params.joint_income) {
          const joint = useJointInsuredStore(pinia);
          body["joint_insured"] = joint.getCreateData(
            quote.joint_insured_quote_id
          );
        }

        if (this.round_robin_agent_id.model) {
          body.round_robin_agent_id = this.round_robin_agent_id.model;
        }

        if (additionalCreateOptions?.questions?.length) {
          body.approved_domain_answers_attributes =
            additionalCreateOptions.questions.map(q => ({
              approved_domain_question_id: q.id,
              answer: q.answer,
            }));
        }

        if (pinia.state.value["chat"]) {
          const chatStore = (await import("#src/stores/chat.js")).useChatStore(
            pinia
          );
          body.chat.initiated_step = chatStore.initiatedStep;
          body.chat.enabled = Boolean(instance.chat);
          body.chat.opened = chatStore.opened;
          body.chat.created_via_chat = chatStore.createdFromChat;
        }

        if (body.chat.created_via_chat || this.refer) {
          body.state = quoting.params.state;
        }

        if (this.refer) {
          if (instance.show_refer_text)
            body.send_refer_text = this.send_refer_text.model;
          body.refer_notes = this.refer_notes.model;
          body.purpose_of_insurance = this.purpose_of_insurance.model;
          body.best_time_to_call = this.best_time_to_call.model;
        }

        if (instance.referData?.referral_uid) {
          body.referral_uid = instance.referData.referral_uid;
        } else if (instance.referData) {
          body.referrer_id = instance.referData.referrer_id;
          body.referrer_type = instance.referData.referrer_type;
        }

        const eApp = await eAppService.createElectronicApplication(body);
        await this.initializeEAppData(eApp, quote);

        this.has_created_eapp = true;

        return { uuid: this.uuid, apply_link: this.apply_link };
      },
      async createAndPrefill(quote = {}, additionalCreateOptions = {}) {
        const instance = useInstanceSettingsStore(pinia);
        await this.createElectronicApplication(quote, additionalCreateOptions);

        if (
          instance.primaryParams &&
          Object.keys(instance.primaryParams).length
        ) {
          const { PrefillService } = await import(
            "#src/services/prefill.service.js"
          );
          const prefillService = new PrefillService(pinia);
          const params = {
            ...instance.primaryParams,
            ...instance.overridePrefillBehavior,
          };

          await prefillService.postCreatePrefill(
            {
              insured: params,
              ...params,
            },
            { skipSetInsuredValues: true, canChangeStep: false }
          );
        }
      },
      saveAssistance() {
        return this.updateEapp({
          assist: {
            top_priority: this.assistance.topPriority,
            ltc_type: this.assistance.ltcType,
          },
        });
      },
      async saveQuoteStep(quote) {
        const insured = usePrimaryInsuredStore(pinia);
        const jInsured = useJointInsuredStore(pinia);

        const electronic_application = {
          rank: quote.rank,
        };

        if (jInsured.id) {
          await this.ensureRoleMatchForDualApplicants();
        }

        quote.real_id = await quotesService.setActiveQuote(
          quote.id,
          insured.id
        );

        if (this.isDualInsured) {
          quote.real_joint_id = await quotesService.setActiveQuote(
            quote.joint_insured_quote_id,
            jInsured.id
          );
        }

        const result = await eAppService.updateElectronicApplication(
          electronic_application
        );

        this.setQuoteMetadata(quote);

        return result;
      },
      async initializeApplicationData(eApp, birthdate = null) {
        this.has_logged_in = true;
        const instance = useInstanceSettingsStore(pinia);
        const flow = useFlowStore(pinia);
        flow.updateStepRequirements(instance.data_requirements);
        // When a case is present, only case: {id, uuid} and birthdate are available.
        // Otherwise, it's the full eApp
        if (!eApp.case?.id || !eApp.case?.uuid) {
          return this.initializeEAppData(eApp);
        }

        const caseStore = useCaseStore(pinia);
        caseStore.id = eApp.case.id;
        caseStore.uuid = eApp.case.uuid;
        return caseStore.loginToCase(birthdate || eApp.birthdate);
      },
      async initializeEAppData(eApp, fullQuote = null) {
        this.setIds(eApp);
        this.apply_link = eApp.apply_link;

        const instance = useInstanceSettingsStore(pinia);
        const quoting = useQuotingStore(pinia);
        const flow = useFlowStore(pinia);

        instance.domain_key = eApp.approved_domain?.domain_key;
        instance.id = eApp.approved_domain?.id;
        instance.domain_key = eApp.approved_domain?.domain_key;
        instance.agent = setAgentFromRequest(eApp.agent);
        if (eApp.product_types) {
          instance.products = setApprovedDomainProductsFromRequest(
            eApp.product_types
          );
        }
        this.initializeParties(eApp.parties);

        let primaryQuote, jointQuote;
        eApp.parties.forEach(p => {
          if (p.application_roles.some(r => r.role === ROLE_TYPE.INSURED)) {
            primaryQuote = InternalQuote(p.quote); // These are Partial InternalQuotes
          }
          if (p.application_roles.some(r => r.role === ROLE_TYPE.JOINT_INSURED)) {
            jointQuote = InternalQuote(p.quote); // These are Partial InternalQuotes
          }
        });

        // an eApp returns partial quote data so we need this workaround to keep everything in sync on the quote page.
        if (fullQuote?.id) {
          this.initializeQuoteData(fullQuote, primaryQuote, jointQuote);
        } else {
          this.initializeQuoteData(primaryQuote, primaryQuote, jointQuote);
        }

        this.initializeExistingCoverages(
          eApp.electronic_application_cases || []
        );

        if (eApp.chat.opened) {
          const chat = await import("#src/stores/chat.js");
          const chatStore = chat.useChatStore(pinia);
          chatStore.setChatFromElectronicApplication(eApp.chat || {});
        }

        this.initializeFromEApp(eApp);
        this.initializeCalculatorFromEapp(eApp);

        quoting.storeQuoteParametersFromEApp(eApp, primaryQuote);

        if (
          instance.primaryParams &&
          Object.keys(instance.primaryParams).length
        ) {
          const { QuotingPrefillService } = await import(
            "#src/services/quoting-prefill.service.js"
          );
          const quotingPrefill = new QuotingPrefillService(pinia);
          quotingPrefill.setRequestPreferencesFromPrefill(
            instance.primaryParams
          );
        }

        flow.updateStepRequirements(eApp.data_requirements);
      },
    },
  })(pinia, hot);
}
